import uniqBy from 'lodash/uniqBy'

export type Position_Array =
  | { type: 'first' }
  | { type: 'last' }
  | { type: 'before'; targetId: string }
  | { type: 'after'; targetId: string }
  | { type: 'index'; index: number }

export type InsertIntoArrayParams<Item> = {
  array: Item[]
  position: Position_Array
  item: Item
  getItemId: (item: Item) => string
}

export function insertIntoArray<Item>({
  array,
  item,
  getItemId,
  position,
}: InsertIntoArrayParams<Item>): Item[] {
  let result: Item[]

  switch (position.type) {
    case 'first':
      result = [item, ...array]
      break
    case 'last':
      result = [...array, item]
      break
    case 'before': {
      let arrayHasTargetId = false

      result = array.reduce<Item[]>((acc, curr, index, { length }) => {
        // This sentence checks if the array contains that id in fact, if not we're doing something wrong and an error is thrown
        if (
          index === length - 1 &&
          !arrayHasTargetId &&
          position.targetId !== getItemId(curr)
        ) {
          throw new Error(
            `This array does not include the target id: ${position.targetId}`
          )
        }

        if (getItemId(curr) === position.targetId) {
          arrayHasTargetId = true
          return [...acc, item, curr]
        }
        return [...acc, curr]
      }, [])
      break
    }
    case 'after': {
      let arrayHasTargetId = false

      result = array.reduce<Item[]>((acc, curr, index, { length }) => {
        // This sentence checks if the array contains that id in fact, if not we're doing something wrong and an error is thrown
        if (
          index === length - 1 &&
          !arrayHasTargetId &&
          position.targetId !== getItemId(curr)
        ) {
          throw new Error(
            `This array does not include the target id: ${position.targetId}`
          )
        }

        if (getItemId(curr) === position.targetId) {
          arrayHasTargetId = true
          return [...acc, curr, item]
        }
        return [...acc, curr]
      }, [])
      break
    }
    case 'index': {
      const targetIndex = Math.min(position.index, array.length)
      result = [
        ...array.slice(0, targetIndex),
        item,
        ...array.slice(targetIndex),
      ]
      break
    }
    default:
      throw new Error(`Invalid position type: ${position}`)
  }

  return uniqBy(result, getItemId)
}
